import React, { useState, useEffect } from 'react';
import { Eye, Users, Building, Calendar, Search, X, Heart, MessageCircle, Star, Image as ImageIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { CandidateWithSlides, Evaluator } from '../../lib/supabase';
import EvaluationDetail from './EvaluationDetail';
import AdminPromotionForm from '../Auth/AdminPromotionForm';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

export default function EvaluationList({ navKey }: { navKey?: number }) {
  const [candidates, setCandidates] = useState<CandidateWithSlides[]>([]);
  const [evaluator, setEvaluator] = useState<Evaluator | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateWithSlides | null>(null);
  const [showAdminPromotion, setShowAdminPromotion] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCandidates, setFilteredCandidates] = useState<CandidateWithSlides[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [likeCounts, setLikeCounts] = useState<Record<string, number>>({});
  const [participateCounts, setParticipateCounts] = useState<Record<string, number>>({});
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const [myRatings, setMyRatings] = useState<Record<string, number>>({});
  const [avgRatings, setAvgRatings] = useState<Record<string, number>>({});
  const [ratingCounts, setRatingCounts] = useState<Record<string, number>>({});
  const [sortBy, setSortBy] = useState<'comments' | 'likes' | 'participants' | 'newest'>('comments');
  const [ownCandidateId, setOwnCandidateId] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterCandidates();
  }, [candidates, searchTerm]);

  // When navigation key changes (e.g., user clicks "発表一覧" in header), reset detail view
  useEffect(() => {
    // Reset detail view and refresh data when user navigates to list
    setSelectedCandidate(null);
    loadData();
  }, [navKey]);

  // Refresh when window/tab regains focus to show latest scores
  useEffect(() => {
    const onFocus = () => {
      // Only refresh when on list view
      if (!selectedCandidate) loadData();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCandidate]);

  const loadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Load evaluator data
      let evaluatorData: Evaluator | null = null;
      {
        const { data, error } = await supabase
          .from('evaluators')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();
        if (error) throw error;
        evaluatorData = data as Evaluator | null;
      }
      // If evaluator not found, derive from candidate profile and create
      if (!evaluatorData) {
        const { data: candidateByEmail } = await supabase
          .from('candidates')
          .select('*')
          .eq('email', user.email)
          .maybeSingle();
        if (candidateByEmail) {
          const { error: createErr } = await supabase
            .from('evaluators')
            .insert({
              name: candidateByEmail.name,
              username: candidateByEmail.username || null,
              email: candidateByEmail.email,
              affiliation: candidateByEmail.affiliation,
              department: candidateByEmail.department || null,
              is_grad_student: candidateByEmail.is_grad_student || false,
              grad_program: candidateByEmail.grad_program || null,
              grad_year: candidateByEmail.grad_year || null
            });
          if (createErr) throw createErr;
          // Re-fetch evaluator
          const { data } = await supabase
            .from('evaluators')
            .select('*')
            .eq('email', user.email)
            .single();
          evaluatorData = data as Evaluator;
        } else {
          throw new Error('評価者情報の作成に失敗しました');
        }
      }
      setEvaluator(evaluatorData);

      // Determine own candidate id if logged-in user is also a presenter
      const { data: ownCand } = await supabase
        .from('candidates')
        .select('id')
        .eq('email', user.email)
        .maybeSingle();
      if (ownCand?.id) setOwnCandidateId(ownCand.id);

      // Load candidates with slides
      const { data: candidatesData, error: candidatesError } = await supabase
        .from('candidates')
        .select(`
          *,
          slide_images (*)
        `)
        .order('created_at', { ascending: false });

      if (candidatesError) throw candidatesError;
      setCandidates(candidatesData || []);

      // Load like counts for all candidates
      try {
        const { data: likesData } = await supabase
          .from('evaluations')
          .select('candidate_id')
          .eq('liked', true);
        const counts: Record<string, number> = {};
        (likesData || []).forEach((row: any) => {
          const id = row.candidate_id as string;
          counts[id] = (counts[id] || 0) + 1;
        });
        setLikeCounts(counts);
      } catch (e) {
        // Non-fatal; ignore like count errors
      }

      // Load participate counts
      try {
        const { data: partsData } = await supabase
          .from('evaluations')
          .select('candidate_id')
          .eq('want_to_participate', true);
        const counts: Record<string, number> = {};
        (partsData || []).forEach((row: any) => {
          const id = row.candidate_id as string;
          counts[id] = (counts[id] || 0) + 1;
        });
        setParticipateCounts(counts);
      } catch {}

      // Load comment counts per candidate
      try {
        const { data: evals } = await supabase
          .from('evaluations')
          .select('id, candidate_id, rating');
        const evalToCandidate = new Map<string, string>();
        (evals || []).forEach((e: any) => evalToCandidate.set(e.id, e.candidate_id));

        const { data: comments } = await supabase
          .from('evaluation_comments')
          .select('evaluation_id');
        const counts: Record<string, number> = {};
        (comments || []).forEach((r: any) => {
          const cid = evalToCandidate.get(r.evaluation_id);
          if (cid) counts[cid] = (counts[cid] || 0) + 1;
        });
        setCommentCounts(counts);
        // Also compute rating averages (rating > 0 only)
        const rCounts: Record<string, number> = {};
        const rSums: Record<string, number> = {};
        (evals || []).forEach((e: any) => {
          const cid = e.candidate_id as string;
          const r = Math.max(0, Math.min(5, e.rating || 0));
          if (r > 0) {
            rCounts[cid] = (rCounts[cid] || 0) + 1;
            rSums[cid] = (rSums[cid] || 0) + r;
          }
        });
        const rAvg: Record<string, number> = {};
        Object.keys(rSums).forEach(cid => {
          const count = rCounts[cid] || 0;
          rAvg[cid] = count > 0 ? rSums[cid] / count : 0;
        });
        setRatingCounts(rCounts);
        setAvgRatings(rAvg);
      } catch {}

      // Load my own ratings per candidate (to show on list)
      try {
        if (evaluatorData) {
          const { data: myEvals } = await supabase
            .from('evaluations')
            .select('candidate_id, rating')
            .eq('evaluator_id', evaluatorData.id);
          const map: Record<string, number> = {};
          (myEvals || []).forEach((row: any) => {
            const r = Math.max(0, Math.min(5, row.rating || 0));
            if (r > 0) map[row.candidate_id] = r;
          });
          setMyRatings(map);
        }
      } catch {}

      // Preselect candidate if coming from My Page link
      try {
        const presetId = localStorage.getItem('selectedCandidateId');
        if (presetId && candidatesData) {
          const preset = (candidatesData as any[]).find(c => c.id === presetId);
          if (preset) {
            setSelectedCandidate(preset as any);
          }
          localStorage.removeItem('selectedCandidateId');
        }
      } catch {}
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filterCandidates = () => {
    if (!searchTerm.trim()) {
      setFilteredCandidates(candidates);
      return;
    }

    const filtered = candidates.filter(candidate => {
      const searchLower = searchTerm.toLowerCase();
      return (
        candidate.name.toLowerCase().includes(searchLower) ||
        candidate.affiliation.toLowerCase().includes(searchLower) ||
        candidate.research_topic.toLowerCase().includes(searchLower) ||
        candidate.research_overview.toLowerCase().includes(searchLower)
      );
    });

    setFilteredCandidates(filtered);
  };

  const getKeySlideUrl = (candidate: CandidateWithSlides) => {
    const keySlide = candidate.slide_images?.find(
      (slide) => slide.slide_index === candidate.key_slide_index
    );
    // Fallback: if key slide not set/missing, use first available image
    return keySlide?.image_url || candidate.slide_images?.[0]?.image_url;
  };

  const handleAdminPromotionSuccess = () => {
    // Reload the page to update user type
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (showAdminPromotion) {
    return (
      <AdminPromotionForm
        onSuccess={handleAdminPromotionSuccess}
        onCancel={() => setShowAdminPromotion(false)}
      />
    );
  }

  if (selectedCandidate) {
    return (
      <EvaluationDetail
        candidate={selectedCandidate}
        evaluator={evaluator!}
        isOwn={ownCandidateId === selectedCandidate.id}
        onBack={() => { setSelectedCandidate(null); loadData(); }}
        onEvaluationSubmitted={loadData}
      />
    );
  }

  // Sort: own research on top if presenter, then by selected metric
  const sortedCandidates = [...filteredCandidates].sort((a, b) => {
    if (ownCandidateId) {
      if (a.id === ownCandidateId && b.id !== ownCandidateId) return -1;
      if (b.id === ownCandidateId && a.id !== ownCandidateId) return 1;
    }
    if (sortBy === 'comments') return (commentCounts[b.id] || 0) - (commentCounts[a.id] || 0);
    if (sortBy === 'likes') return (likeCounts[b.id] || 0) - (likeCounts[a.id] || 0);
    if (sortBy === 'participants') return (participateCounts[b.id] || 0) - (participateCounts[a.id] || 0);
    const ta = (a as any).created_at ? new Date((a as any).created_at).getTime() : 0;
    const tb = (b as any).created_at ? new Date((b as any).created_at).getTime() : 0;
    return tb - ta;
  });

  return (
    <div className="space-y-6">
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">研究発表一覧</h1>
          <p className="text-muted-foreground mt-1">
            {filteredCandidates.length}件の研究発表
            {searchTerm && ` (全${candidates.length}件中)`}
          </p>
        </div>
      </div>

      {/* Search Bar + Sort */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="研究者名・所属・研究課題・キーワードで検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-10"
              />
              {searchTerm && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1 h-8 w-8 p-0"
                  onClick={() => setSearchTerm('')}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">並び替え:</span>
              <select
                className="h-9 border rounded-md px-2 bg-background"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
              >
                <option value="comments">コメント数</option>
                <option value="likes">いいね数</option>
                <option value="participants">参加希望数</option>
                <option value="newest">新着</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {filteredCandidates.length === 0 ? (
        <Card>
          <CardContent className="text-center py-12">
            {searchTerm ? (
              <>
                <Search className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">検索条件に一致する研究発表が見つかりませんでした</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setSearchTerm('')}
                >
                  検索をクリア
                </Button>
              </>
            ) : (
              <>
                <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">まだ研究発表が登録されていません</p>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {sortedCandidates.map((candidate) => {
            const keySlideUrl = getKeySlideUrl(candidate);
            return (
              <Card
                key={candidate.id}
                className="hover:shadow-md transition-all duration-200 cursor-pointer group"
                onClick={() => setSelectedCandidate(candidate)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="h-12 w-16 bg-muted rounded border overflow-hidden flex items-center justify-center">
                      {keySlideUrl ? (
                        <img src={keySlideUrl} alt="キースライド" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="text-base font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                        {candidate.research_topic}
                      </h3>
                      <div className="mt-1 flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{candidate.name}</span>
                        </div>
                        <div className="flex items-center">
                          <Building className="h-4 w-4 mr-2 flex-shrink-0" />
                          <span className="truncate">{candidate.affiliation}</span>
                        </div>
                      </div>
                        <div className="mt-3">
                          <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
                          <div className="flex items-center text-sm text-muted-foreground">
                            <div className="flex items-center mr-2">
                              {(() => {
                                const avg = avgRatings[candidate.id] || 0;
                                const rounded = Math.max(0, Math.min(5, Math.round(avg * 2) / 2));
                                return [1,2,3,4,5].map(i => {
                                  const fill = rounded >= i ? 1 : (rounded >= i - 0.5 ? 0.5 : 0);
                                  if (fill === 1) {
                                    return (
                                      <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                        <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                      </svg>
                                    );
                                  }
                                  if (fill === 0.5) {
                                    return (
                                      <span key={i} className="relative inline-block h-4 w-4 align-middle">
                                        <svg viewBox="0 0 24 24" className="absolute inset-0 h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                          <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                        </svg>
                                        <span className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
                                          <svg viewBox="0 0 24 24" className="h-4 w-4 text-yellow-500" fill="currentColor" aria-hidden="true">
                                            <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                          </svg>
                                        </span>
                                      </span>
                                    );
                                  }
                                  return (
                                    <svg key={i} viewBox="0 0 24 24" className="h-4 w-4 text-muted-foreground" fill="currentColor" aria-hidden="true">
                                      <path d="M12 .587l3.668 7.431 8.2 1.193-5.934 5.787 1.401 8.168L12 18.896l-7.335 3.87 1.401-8.168L.132 9.211l8.2-1.193z"/>
                                    </svg>
                                  );
                                });
                              })()}
                            </div>
                            <span>
                              {(avgRatings[candidate.id] || 0).toFixed(1)}
                              <span className="ml-1 text-xs text-muted-foreground">({ratingCounts[candidate.id] || 0})</span>
                            </span>
                          </div>
                          {(myRatings[candidate.id] || 0) > 0 && (
                            <div className="flex items-center text-xs text-muted-foreground">
                              <span className="mr-1">あなた:</span>
                              <Star className="h-3 w-3 mr-0.5 text-yellow-500" />
                              <span>{myRatings[candidate.id]}</span>
                            </div>
                          )}
                          <div className="flex items-center text-sm text-muted-foreground">
                            <MessageCircle className="h-4 w-4 mr-1 text-primary" />
                            <span>{commentCounts[candidate.id] || 0}</span>
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Heart className="h-4 w-4 mr-1 text-red-500" />
                            <span>{likeCounts[candidate.id] || 0}</span>
                          </div>
                          <div className="flex items-center text-sm text-muted-foreground">
                            <Users className="h-4 w-4 mr-1 text-green-600" />
                            <span>{participateCounts[candidate.id] || 0}</span>
                          </div>
                          <Button variant="ghost" size="sm" className="text-primary hover:text-primary/80 whitespace-nowrap">
                            <Eye className="h-4 w-4 mr-1" />
                            詳細
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
